/*
  Queen Tape — product page videos
  Every theme video on product pages plays muted on a loop, all the time:
  no pause, no play button, no timer, no volume, no fullscreen, and no black box
  while it loads. Videos inside review/app blocks (.shopify-app-block) are left alone.

  Covers: QT Video slider + QT Story, Shrine video slides / video blocks (<internal-video>),
  product gallery videos, and plain <video> tags in Custom Liquid sections.
*/
(() => {
  if (window.__qtVideoLoop) return;
  window.__qtVideoLoop = true;

  const prepared = new WeakSet();
  const scope = () => document.getElementById('MainContent') || document.body;

  const isManaged = (v) =>
    v instanceof HTMLVideoElement &&
    !v.closest('.shopify-app-block, [data-qt-video-ignore]') &&
    scope().contains(v);

  // The area that has to come near the screen before its videos start.
  // A whole section, so slides that sit off to the side in a slider start too.
  const areaFor = (v) => v.closest('.shopify-section') || v.parentElement || v;

  const isNear = (el) => {
    const r = el.getBoundingClientRect();
    const h = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > -h && r.top < h * 2;
  };

  function play(v) {
    if (!v.paused || v.ended && !v.loop) return;
    v.muted = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }

  function playArea(area) {
    area.querySelectorAll('video').forEach((v) => {
      if (isManaged(v)) play(v);
    });
  }

  // Keep playing if anything (a tap, the browser, other scripts) pauses it.
  function onPause(e) {
    const v = e.currentTarget;
    if (document.hidden) return;
    const now = performance.now();
    v.__qtRetry = v.__qtLast && now - v.__qtLast < 1500 ? (v.__qtRetry || 0) + 1 : 0;
    v.__qtLast = now;
    if (v.__qtRetry > 4) return; // e.g. phone power saving — retried on the next tap or scroll
    if (isNear(v)) requestAnimationFrame(() => play(v));
  }

  const nearObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => entries.forEach((entry) => entry.isIntersecting && playArea(entry.target)),
          { rootMargin: '100% 0px 100% 0px' }
        )
      : null;

  function prepare(v) {
    if (prepared.has(v) || !isManaged(v)) return;
    prepared.add(v);

    v.muted = true;
    v.defaultMuted = true;
    v.loop = true;
    v.autoplay = true;
    v.playsInline = true;
    v.controls = false;
    v.disablePictureInPicture = true;
    ['muted', 'loop', 'autoplay', 'playsinline', 'webkit-playsinline', 'disablepictureinpicture', 'disableremoteplayback'].forEach((a) =>
      v.setAttribute(a, '')
    );
    v.removeAttribute('controls');
    v.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
    v.setAttribute('tabindex', '-1');

    let reload = false;

    // Shrine lazy-loads its sources until the video scrolls into view (that's the black box).
    v.querySelectorAll('source[data-src]').forEach((s) => {
      s.setAttribute('src', s.getAttribute('data-src'));
      s.removeAttribute('data-src');
      reload = true;
    });

    // No poster image? Ask the browser to show the first frame instead of black.
    if (!v.getAttribute('poster')) {
      const addFrame = (el) => {
        const src = el.getAttribute('src');
        if (src && !src.includes('#') && !src.startsWith('blob:')) {
          el.setAttribute('src', `${src}#t=0.001`);
          reload = true;
        }
      };
      addFrame(v);
      v.querySelectorAll('source').forEach(addFrame);
    }

    if (v.preload === 'none' || !v.getAttribute('preload')) v.preload = 'metadata';
    if (reload) v.load();

    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', () => {
      if (!v.muted) v.muted = true;
    });

    const area = areaFor(v);
    if (nearObserver) nearObserver.observe(area);
    if (isNear(area)) play(v);
  }

  // Shrine's player: stop its own lazy-load observer (it reloads the video = flash) and its
  // play/pause button handler.
  function tameShrinePlayers(root) {
    root.querySelectorAll('internal-video').forEach((iv) => {
      if (iv.closest('.shopify-app-block')) return;
      if (iv.observer && iv.observer.disconnect) iv.observer.disconnect();
      iv.classList.add('internal-video--playing', 'internal-video--muted');
      iv.classList.remove('internal-video--loading');
    });
  }

  function scan(root) {
    (root.querySelectorAll ? root : document).querySelectorAll('video').forEach(prepare);
    tameShrinePlayers(root.querySelectorAll ? root : document);
  }

  // Taps on Shrine's player area never reach its pause handler.
  document.addEventListener(
    'click',
    (e) => {
      const iv = e.target.closest && e.target.closest('internal-video');
      if (!iv || iv.closest('.shopify-app-block')) return;
      if (e.target.closest('.internal-video__play, .internal-video__sound-btn, .internal-video__timeline, .internal-video__fullscreen-btn')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  function init() {
    scan(document);
    if (customElements && customElements.whenDefined) {
      customElements.whenDefined('internal-video').then(() => tameShrinePlayers(document));
    }

    // Videos added later (product gallery, theme editor, sliders that re-render).
    new MutationObserver((records) => {
      records.forEach((r) =>
        r.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'VIDEO') prepare(n);
          else if (n.querySelector && n.querySelector('video, internal-video')) scan(n);
        })
      );
    }).observe(document.body, { childList: true, subtree: true });

    // Phones in power-saving mode block autoplay until the first tap; start everything then.
    const kick = () => document.querySelectorAll('video').forEach((v) => prepared.has(v) && isNear(v) && play(v));
    ['touchstart', 'pointerdown', 'keydown'].forEach((t) => document.addEventListener(t, kick, { passive: true }));
    document.addEventListener('visibilitychange', () => !document.hidden && kick());
    window.addEventListener('pageshow', kick);
    document.addEventListener('shopify:section:load', (e) => scan(e.target));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
